import type { TFunction } from 'i18next';
import type { PlanAllowance } from '@/api';
import { formatNumber } from '@/lib/money';

/**
 * Billable services from `backend/formshive/src/rates.rs` (`SERVICE_RATES`).
 * `rateVfiat` is per unit; file uploads are counted in KB.
 */
export const USAGE_SERVICES: Record<
  string,
  { labelKey: string; noteKey?: string; nounKey: string; rateVfiat: number; unit: 'count' | 'kb' }
> = {
  message: {
    labelKey: 'billing.usage.message',
    nounKey: 'billing.allowanceNoun.message',
    rateVfiat: 500,
    unit: 'count',
  },
  filter_spam: {
    labelKey: 'billing.usage.filter_spam',
    nounKey: 'billing.allowanceNoun.filter_spam',
    rateVfiat: 500,
    unit: 'count',
  },
  email_notification: {
    labelKey: 'billing.usage.email_notification',
    noteKey: 'billing.usage.email_notificationNote',
    nounKey: 'billing.allowanceNoun.email_notification',
    rateVfiat: 300,
    unit: 'count',
  },
  email_responder: {
    labelKey: 'billing.usage.email_responder',
    noteKey: 'billing.usage.email_responderNote',
    nounKey: 'billing.allowanceNoun.email_responder',
    rateVfiat: 300,
    unit: 'count',
  },
  integration: {
    labelKey: 'billing.usage.integration',
    noteKey: 'billing.usage.integrationNote',
    nounKey: 'billing.allowanceNoun.integration',
    rateVfiat: 50,
    unit: 'count',
  },
  file_upload: {
    labelKey: 'billing.usage.file_upload',
    nounKey: 'billing.allowanceNoun.file_upload',
    rateVfiat: 1,
    unit: 'kb',
  },
};

export const USAGE_ORDER = Object.keys(USAGE_SERVICES);

/** Movement `features` strings written by the backend ledger. */
export type MovementKind =
  | 'usage'
  | 'excess_usage'
  | 'deposit'
  | 'welcome_credit'
  | 'referral_referrer'
  | 'referral_referee'
  | 'assistant'
  | 'subscription'
  | 'refund'
  | 'other';

export function movementKind(features: string | null | undefined): MovementKind {
  const f = features ?? '';
  if (f === 'service_usage') return 'usage';
  if (f === 'excess_usage') return 'excess_usage';
  if (f === 'deposit' || f.startsWith('deposit:')) return 'deposit';
  if (f === 'welcome_credit') return 'welcome_credit';
  if (f.startsWith('referral_bonus_referrer')) return 'referral_referrer';
  if (f.startsWith('referral_bonus_referee')) return 'referral_referee';
  if (f.startsWith('tokens:')) return 'assistant';
  if (f === 'subscription' || f.startsWith('subscription:')) return 'subscription';
  if (f === 'refund' || f.startsWith('refund:') || f.startsWith('dispute:')) return 'refund';
  return 'other';
}

/** Usage rows are grouped per day in History. */
export function isUsageKind(kind: MovementKind): boolean {
  return kind === 'usage' || kind === 'excess_usage';
}

export function movementLabel(kind: MovementKind, raw: string, t: TFunction): string {
  return kind === 'other' ? raw : t(`billing.movement.${kind}`);
}

export function movementDetail(kind: MovementKind, t: TFunction): string {
  return kind === 'other' ? '' : t(`billing.movementDetail.${kind}`, { defaultValue: '' });
}

/** "10 messages, spam checks and notification emails · 50 MB of uploads". */
export function allowanceSummary(allowances: PlanAllowance[], t: TFunction, lang: string): string {
  const byLimit = new Map<number, string[]>();
  let files: number | null = null;
  for (const a of allowances) {
    const svc = USAGE_SERVICES[a.service];
    if (!svc) continue;
    if (svc.unit === 'kb') {
      files = a.limit;
      continue;
    }
    const list = byLimit.get(a.limit) ?? [];
    list.push(t(svc.nounKey));
    byLimit.set(a.limit, list);
  }
  const parts: string[] = [];
  for (const [limit, nouns] of byLimit) {
    const joined =
      nouns.length > 1
        ? t('billing.allowanceJoin', {
            first: nouns.slice(0, -1).join(', '),
            last: nouns[nouns.length - 1],
          })
        : nouns[0];
    parts.push(
      limit < 0
        ? t('billing.allowanceUnlimited', { what: joined })
        : t('billing.allowanceCount', {
            count: limit,
            n: formatNumber(limit, lang),
            what: joined,
          })
    );
  }
  if (files !== null) {
    parts.push(
      files < 0
        ? t('billing.allowanceFilesUnlimited')
        : t('billing.allowanceFiles', { mb: formatNumber(files / 1024, lang) })
    );
  }
  return parts.join(' · ');
}

/** "Free Plan" from the backend reads "Free"; the copy around it adds the word. */
export function planLabel(name: string): string {
  return name.replace(/\s+plan$/i, '');
}
